import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useMeta from "@/hooks/useMeta";
import { news } from "@/mocks/news";
import { TYPE_LABELS, TYPE_PATHS } from "@/types/media";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [copied, setCopied] = useState(false);

  const item = useMemo(() => news.find((n) => n.id === id), [id]);

  useMeta(
    item
      ? {
          title: `${item.title} - 大象传媒`,
          description: item.summary,
          keywords: `${item.tags.join(",")},影视资讯,大象传媒`,
        }
      : {
          title: "资讯内容不存在 - 大象传媒",
          description: "您访问的资讯内容不存在或已下线，请返回资讯列表浏览更多内容。",
          keywords: "大象传媒,影视资讯",
        }
  );

  const related = useMemo(() => {
    if (!item) return [];
    return news
      .filter((n) => n.id !== item.id)
      .map((n) => ({
        n,
        score:
          (n.category === item.category ? 2 : 0) +
          n.tags.filter((t) => item.tags.includes(t)).length,
      }))
      .sort((a, b) => b.score - a.score || b.n.date.localeCompare(a.n.date))
      .slice(0, 3)
      .map((r) => r.n);
  }, [item]);

  const copyContent = async () => {
    if (!item) return;
    try {
      await navigator.clipboard.writeText(`${item.title}\n\n${item.content.join("\n\n")}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!item) {
    return (
      <div className="mx-auto w-full px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="资讯内容不存在"
          description="您访问的资讯可能已被删除或调整，不妨看看其他内容。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/news"
            className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[{ label: "影视资讯", to: "/news" }, { label: item.title }]}
      />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <span className="inline-flex items-center gap-1 rounded-md bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-700">
            <i className="ri-price-tag-3-line"></i>
            {TYPE_LABELS[item.category]}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {item.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              约 {Math.max(2, item.content.length * 2)} 分钟阅读
            </span>
            <button
              type="button"
              onClick={copyContent}
              className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
              {copied ? "已复制正文" : "复制正文"}
            </button>
          </div>

          <div className="mt-6 h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} 配图`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-6 space-y-4">
            {item.content.map((para, i) => (
              <p key={i} className="text-sm leading-8 text-foreground-700 md:text-base">
                {para}
              </p>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2 border-t border-background-200 pt-5">
            {item.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                # {tag}
              </Link>
            ))}
          </div>
        </article>

        <aside>
          <section className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-600 text-background-50">
                <i className="ri-links-line text-sm"></i>
              </span>
              相关文章
            </h2>
            <div className="flex flex-col gap-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/news/${r.id}`}
                  className="group rounded-md border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span>{r.date}</span>
                    <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                      {TYPE_LABELS[r.category]}
                    </span>
                  </div>
                  <h3 className="mt-2 line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                    {r.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="mb-3 font-heading text-base font-bold text-foreground-950">
              浏览频道
            </h2>
            <div className="flex flex-col gap-2 text-sm">
              {(["movie", "tv", "variety"] as const).map((t) => (
                <Link
                  key={t}
                  to={TYPE_PATHS[t]}
                  className="flex items-center justify-between rounded-md px-3 py-2 text-foreground-700 transition-colors hover:bg-background-200"
                >
                  {TYPE_LABELS[t]}频道
                  <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}